// BlogPage.js - Developer dashboard for blog management
// Full CRUD interface for creating, editing, and publishing blog posts
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import axios from 'axios';
import SidebarNav from '../components/SidebarNav';

// Debug: Log when component renders
console.log('[BlogPage] Component module loaded');

const BlogPage = () => {
    const location = useLocation();
    
    // Debug: Log component render
    console.log('[BlogPage] Component rendering...');

    // --- State Variables ---
    const [posts, setPosts] = useState([]); // All blog posts
    const [loading, setLoading] = useState(true); // Loading state
    const [error, setError] = useState(null); // Error message
    const [success, setSuccess] = useState(null); // Success message
    
    // Editor state
    const [showEditor, setShowEditor] = useState(false); // Show/hide editor
    const [editingPost, setEditingPost] = useState(null); // Post being edited (null = new post)
    const [saving, setSaving] = useState(false); // Saving state
    
    // Form state
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        excerpt: '',
        featuredImage: '',
        featuredImageAlt: '',
        metaTitle: '',
        metaDescription: '',
        keywords: '',
        canonicalUrl: '',
        category: 'General',
        tags: '',
        author: 'Riviera Tech Solutions',
        isPublished: false,
        isFeatured: false,
        displayOrder: 0
    });

    // --- Theming ---
    const textPrimary = "text-silver-light";
    const textSecondary = "text-silver-medium/80";

    // --- Intersection Observer Animations ---
    const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [contentRef, contentInView] = useInView({ triggerOnce: true, threshold: 0.1 });

    // --- Fetch All Posts ---
    const fetchPosts = useCallback(async () => {
        console.log('[BlogPage] Fetching all posts...');
        setLoading(true);
        try {
            const res = await axios.get('/api/blog');
            console.log('[BlogPage] API response:', res.data);
            if (res.data && res.data.success) {
                setPosts(res.data.posts);
            } else {
                throw new Error(res.data.message || 'Failed to fetch posts');
            }
        } catch (err) {
            console.error('[BlogPage] Fetch error:', err);
            setError(`Error fetching posts: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    // --- Initial Data Fetch ---
    useEffect(() => {
        console.log('[BlogPage] Initial mount - fetching posts');
        fetchPosts();
    }, [fetchPosts]);

    // --- Reset Form ---
    const resetForm = () => {
        setFormData({
            title: '',
            content: '',
            excerpt: '',
            featuredImage: '',
            featuredImageAlt: '',
            metaTitle: '',
            metaDescription: '',
            keywords: '',
            canonicalUrl: '',
            category: 'General',
            tags: '',
            author: 'Riviera Tech Solutions',
            isPublished: false,
            isFeatured: false,
            displayOrder: 0
        });
        setEditingPost(null);
    };

    // --- Open Editor for New Post ---
    const handleNewPost = () => {
        console.log('[BlogPage] Opening editor for new post');
        resetForm();
        setShowEditor(true);
        setError(null);
        setSuccess(null);
    };

    // --- Open Editor for Editing ---
    const handleEditPost = async (postId) => {
        console.log('[BlogPage] Opening editor for post:', postId);
        setError(null);
        setSuccess(null);
        
        try {
            // Fetch full post data including content
            const res = await axios.get(`/api/blog/${postId}`);
            if (res.data && res.data.success) {
                const post = res.data.post;
                setEditingPost(post);
                setFormData({
                    title: post.title || '',
                    content: post.content || '',
                    excerpt: post.excerpt || '',
                    featuredImage: post.featuredImage || '',
                    featuredImageAlt: post.featuredImageAlt || '',
                    metaTitle: post.metaTitle || '',
                    metaDescription: post.metaDescription || '',
                    keywords: post.keywords?.join(', ') || '',
                    canonicalUrl: post.canonicalUrl || '',
                    category: post.category || 'General',
                    tags: post.tags?.join(', ') || '',
                    author: post.author || 'Riviera Tech Solutions',
                    isPublished: post.isPublished || false,
                    isFeatured: post.isFeatured || false,
                    displayOrder: post.displayOrder || 0
                });
                setShowEditor(true);
            }
        } catch (err) {
            console.error('[BlogPage] Error fetching post:', err);
            setError(`Error loading post: ${err.message}`);
        }
    };

    // --- Handle Form Input Change ---
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // --- Handle Form Submit (Create/Update) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('[BlogPage] Submitting form...', formData);
        
        // Validate required fields
        if (!formData.title.trim()) {
            setError('Title is required');
            return;
        }
        if (!formData.content.trim()) {
            setError('Content is required');
            return;
        }

        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            // Prepare data - convert comma-separated strings to arrays
            const postData = {
                ...formData,
                keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k),
                tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
                displayOrder: parseInt(formData.displayOrder) || 0
            };

            let res;
            if (editingPost) {
                // Update existing post
                console.log('[BlogPage] Updating post:', editingPost._id);
                res = await axios.put(`/api/blog/${editingPost._id}`, postData);
            } else {
                // Create new post
                console.log('[BlogPage] Creating new post');
                res = await axios.post('/api/blog', postData);
            }

            if (res.data && res.data.success) {
                setSuccess(editingPost ? 'Post updated successfully!' : 'Post created successfully!');
                setShowEditor(false);
                resetForm();
                fetchPosts();
            } else {
                throw new Error(res.data.message);
            }
        } catch (err) {
            console.error('[BlogPage] Save error:', err);
            setError(`Save failed: ${err.response?.data?.message || err.message}`);
        } finally {
            setSaving(false);
        }
    };

    // --- Toggle Publish Status ---
    const handleTogglePublish = async (postId) => {
        console.log('[BlogPage] Toggling publish for:', postId);
        try {
            const res = await axios.patch(`/api/blog/${postId}/publish`);
            if (res.data && res.data.success) {
                // Update local state
                setPosts(prev => prev.map(p => 
                    p._id === postId ? { ...p, isPublished: res.data.post.isPublished } : p
                ));
                setSuccess(`Post ${res.data.post.isPublished ? 'published' : 'unpublished'} successfully!`);
            }
        } catch (err) {
            console.error('[BlogPage] Toggle publish error:', err);
            setError(`Toggle failed: ${err.response?.data?.message || err.message}`);
        }
    };

    // --- Toggle Featured Status ---
    const handleToggleFeatured = async (postId) => {
        console.log('[BlogPage] Toggling featured for:', postId);
        try {
            const res = await axios.patch(`/api/blog/${postId}/feature`);
            if (res.data && res.data.success) {
                // Update local state
                setPosts(prev => prev.map(p => 
                    p._id === postId ? { ...p, isFeatured: res.data.post.isFeatured } : p
                ));
            }
        } catch (err) {
            console.error('[BlogPage] Toggle featured error:', err);
            setError(`Toggle failed: ${err.response?.data?.message || err.message}`);
        }
    };

    // --- Delete Post ---
    const handleDelete = async (postId, title) => {
        if (!window.confirm(`Are you sure you want to delete "${title}"? This cannot be undone.`)) {
            return;
        }
        
        console.log('[BlogPage] Deleting post:', postId);
        try {
            const res = await axios.delete(`/api/blog/${postId}`);
            if (res.data && res.data.success) {
                setSuccess('Post deleted successfully!');
                fetchPosts();
            }
        } catch (err) {
            console.error('[BlogPage] Delete error:', err);
            setError(`Delete failed: ${err.response?.data?.message || err.message}`);
        }
    };

    // --- Format Date ---
    const formatDate = (dateString) => {
        if (!dateString) return 'Not published';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="bg-black min-h-screen font-sans">
            {/* Sidebar */}
            <SidebarNav currentPath={location.pathname} />

            {/* Main Content Area */}
            <div className="ml-64">
                {/* Hero Section */}
                <section
                    ref={heroRef}
                    className="pt-36 pb-16 bg-black"
                >
                    <div className="container mx-auto px-6">
                        <div
                            className={`max-w-3xl mx-auto text-center transition-all duration-1000 transform ${heroInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                        >
                            <h1 className={`text-5xl md:text-6xl font-serif font-medium mb-6 ${textPrimary}`}>
                                Blog Management
                            </h1>
                            <p className={`text-lg md:text-xl ${textSecondary} mb-8 leading-relaxed`}>
                                Create, edit, and publish blog posts with SEO optimization.
                            </p>
                            <div className="mt-3 w-40 h-1 mx-auto bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-50"></div>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section
                    ref={contentRef}
                    className={`py-8 transition-all duration-1000 transform ${contentInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                >
                    <div className="container mx-auto px-6 space-y-8">
                        {/* Alerts */}
                        {error && (
                            <div className="max-w-5xl mx-auto bg-red-900/20 border border-red-700/30 text-red-300 px-5 py-4 rounded-lg">
                                <div className="flex items-center space-x-3">
                                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>{error}</span>
                                    <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">&times;</button>
                                </div>
                            </div>
                        )}
                        {success && (
                            <div className="max-w-5xl mx-auto bg-green-900/20 border border-green-700/30 text-green-300 px-5 py-4 rounded-lg">
                                <div className="flex items-center space-x-3">
                                    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>{success}</span>
                                    <button onClick={() => setSuccess(null)} className="ml-auto text-green-400 hover:text-green-300">&times;</button>
                                </div>
                            </div>
                        )}

                        {/* Action Bar */}
                        <div className="max-w-5xl mx-auto flex justify-between items-center">
                            <div className={`${textSecondary}`}>
                                {posts.length} total posts • {posts.filter(p => p.isPublished).length} published
                            </div>
                            <button
                                onClick={handleNewPost}
                                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-md font-medium transition-colors flex items-center"
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                New Post
                            </button>
                        </div>

                        {/* Blog Posts List */}
                        <div className="max-w-5xl mx-auto bg-gray-900/60 rounded-lg border border-gray-800/50 overflow-hidden shadow-2xl backdrop-blur-sm">
                            <div className="p-6">
                                <h2 className={`text-2xl font-medium mb-6 ${textPrimary} flex items-center`}>
                                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                                    </svg>
                                    All Blog Posts
                                    <span className="ml-2 text-sm font-normal text-gray-400">({posts.length})</span>
                                </h2>

                                {loading ? (
                                    <div className="py-8">
                                        <div className="animate-pulse flex flex-col space-y-4">
                                            <div className="h-16 bg-gray-800/70 rounded-md w-full"></div>
                                            <div className="h-16 bg-gray-800/70 rounded-md w-full"></div>
                                            <div className="h-16 bg-gray-800/70 rounded-md w-full"></div>
                                        </div>
                                    </div>
                                ) : posts.length > 0 ? (
                                    <div className="space-y-3">
                                        {posts.map((post) => (
                                            <div
                                                key={post._id}
                                                className={`p-4 rounded-lg border transition-all ${
                                                    post.isPublished 
                                                        ? 'bg-green-900/10 border-green-500/30' 
                                                        : 'bg-gray-800/30 border-gray-700/30 hover:border-gray-600/50'
                                                }`}
                                            >
                                                <div className="flex items-start justify-between flex-wrap gap-4">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                            <h3 className={`font-medium ${textPrimary}`}>
                                                                {post.title}
                                                            </h3>
                                                            {post.isPublished && (
                                                                <span className="px-2 py-0.5 text-xs bg-green-600 text-white rounded-full">
                                                                    Published
                                                                </span>
                                                            )}
                                                            {post.isFeatured && (
                                                                <span className="px-2 py-0.5 text-xs bg-yellow-600 text-white rounded-full">
                                                                    Featured
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className={`text-sm ${textSecondary} line-clamp-1 mb-1`}>
                                                            {post.excerpt || 'No excerpt'}
                                                        </p>
                                                        <div className="flex items-center gap-3 text-xs text-gray-500">
                                                            <span className="px-2 py-0.5 bg-gray-800/50 rounded">
                                                                {post.category}
                                                            </span>
                                                            <span>
                                                                {formatDate(post.publishedAt || post.createdAt)}
                                                            </span>
                                                            <span>
                                                                {post.readingTime || 1} min read
                                                            </span>
                                                            {post.viewCount > 0 && (
                                                                <span>
                                                                    {post.viewCount} views
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {/* View Live (if published) */}
                                                        {post.isPublished && (
                                                            <a
                                                                href={`/blog/${post.slug}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                                                                title="View live post"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                </svg>
                                                            </a>
                                                        )}
                                                        {/* Edit */}
                                                        <button
                                                            onClick={() => handleEditPost(post._id)}
                                                            className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                                                            title="Edit post"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                        {/* Toggle Publish */}
                                                        <button
                                                            onClick={() => handleTogglePublish(post._id)}
                                                            className={`p-2 transition-colors ${
                                                                post.isPublished 
                                                                    ? 'text-green-400 hover:text-green-300' 
                                                                    : 'text-gray-400 hover:text-green-400'
                                                            }`}
                                                            title={post.isPublished ? 'Unpublish' : 'Publish'}
                                                        >
                                                            <svg className="w-5 h-5" fill={post.isPublished ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                            </svg>
                                                        </button>
                                                        {/* Toggle Featured */}
                                                        <button
                                                            onClick={() => handleToggleFeatured(post._id)}
                                                            className={`p-2 transition-colors ${
                                                                post.isFeatured 
                                                                    ? 'text-yellow-400 hover:text-yellow-300' 
                                                                    : 'text-gray-400 hover:text-yellow-400'
                                                            }`}
                                                            title={post.isFeatured ? 'Remove from featured' : 'Mark as featured'}
                                                        >
                                                            <svg className="w-5 h-5" fill={post.isFeatured ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                                            </svg>
                                                        </button>
                                                        {/* Delete */}
                                                        <button
                                                            onClick={() => handleDelete(post._id, post.title)}
                                                            className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                                                            title="Delete post"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="py-8 text-center">
                                        <svg className="w-16 h-16 mx-auto text-gray-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                                        </svg>
                                        <p className={`${textSecondary} mb-2`}>No blog posts yet.</p>
                                        <p className="text-sm text-gray-500 mb-4">Create your first post to get started!</p>
                                        <button
                                            onClick={handleNewPost}
                                            className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-md font-medium transition-colors"
                                        >
                                            Create First Post
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Editor Modal */}
            {showEditor && (
                <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-gray-900 rounded-lg border border-gray-700 w-full max-w-4xl my-8 shadow-2xl">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-700">
                            <h3 className={`text-xl font-medium ${textPrimary}`}>
                                {editingPost ? 'Edit Blog Post' : 'Create New Blog Post'}
                            </h3>
                            <button
                                onClick={() => { setShowEditor(false); resetForm(); }}
                                className="text-gray-400 hover:text-white transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Modal Body - Form */}
                        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                            {/* Basic Info Section */}
                            <div className="space-y-4">
                                <h4 className={`text-sm font-medium ${textSecondary} uppercase tracking-wider`}>Basic Information</h4>
                                
                                {/* Title */}
                                <div>
                                    <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                        Title *
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        placeholder="Enter blog post title..."
                                        className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                        required
                                    />
                                </div>

                                {/* Content */}
                                <div>
                                    <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                        Content * (HTML supported)
                                    </label>
                                    <textarea
                                        name="content"
                                        value={formData.content}
                                        onChange={handleInputChange}
                                        placeholder="Write your blog post content here... HTML tags are supported for formatting."
                                        rows={12}
                                        className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-3 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono text-sm"
                                        required
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Tip: Use &lt;p&gt;, &lt;h2&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;a href=""&gt; tags for formatting.
                                    </p>
                                </div>

                                {/* Excerpt */}
                                <div>
                                    <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                        Excerpt (shown in blog listing)
                                    </label>
                                    <textarea
                                        name="excerpt"
                                        value={formData.excerpt}
                                        onChange={handleInputChange}
                                        placeholder="Brief summary of the post (max 300 characters)..."
                                        rows={3}
                                        maxLength={300}
                                        className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">{formData.excerpt.length}/300 characters</p>
                                </div>
                            </div>

                            {/* Media Section */}
                            <div className="space-y-4 pt-4 border-t border-gray-800">
                                <h4 className={`text-sm font-medium ${textSecondary} uppercase tracking-wider`}>Featured Image</h4>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                            Image URL
                                        </label>
                                        <input
                                            type="text"
                                            name="featuredImage"
                                            value={formData.featuredImage}
                                            onChange={handleInputChange}
                                            placeholder="https://example.com/image.jpg or /path/to/image.jpg"
                                            className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                            Alt Text (for SEO)
                                        </label>
                                        <input
                                            type="text"
                                            name="featuredImageAlt"
                                            value={formData.featuredImageAlt}
                                            onChange={handleInputChange}
                                            placeholder="Describe the image..."
                                            className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Organization Section */}
                            <div className="space-y-4 pt-4 border-t border-gray-800">
                                <h4 className={`text-sm font-medium ${textSecondary} uppercase tracking-wider`}>Organization</h4>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                            Category
                                        </label>
                                        <input
                                            type="text"
                                            name="category"
                                            value={formData.category}
                                            onChange={handleInputChange}
                                            placeholder="e.g., Technology, Business, Tutorial"
                                            className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                            Tags (comma-separated)
                                        </label>
                                        <input
                                            type="text"
                                            name="tags"
                                            value={formData.tags}
                                            onChange={handleInputChange}
                                            placeholder="web development, react, nodejs"
                                            className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                            Author
                                        </label>
                                        <input
                                            type="text"
                                            name="author"
                                            value={formData.author}
                                            onChange={handleInputChange}
                                            placeholder="Author name"
                                            className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* SEO Section */}
                            <div className="space-y-4 pt-4 border-t border-gray-800">
                                <h4 className={`text-sm font-medium ${textSecondary} uppercase tracking-wider`}>SEO Settings</h4>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                            Meta Title (max 70 chars)
                                        </label>
                                        <input
                                            type="text"
                                            name="metaTitle"
                                            value={formData.metaTitle}
                                            onChange={handleInputChange}
                                            placeholder="Custom title for search results (optional)"
                                            maxLength={70}
                                            className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">{formData.metaTitle.length}/70</p>
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                            Keywords (comma-separated)
                                        </label>
                                        <input
                                            type="text"
                                            name="keywords"
                                            value={formData.keywords}
                                            onChange={handleInputChange}
                                            placeholder="web development, SEO, business"
                                            className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                        Meta Description (max 160 chars)
                                    </label>
                                    <textarea
                                        name="metaDescription"
                                        value={formData.metaDescription}
                                        onChange={handleInputChange}
                                        placeholder="Custom description for search results (optional)"
                                        rows={2}
                                        maxLength={160}
                                        className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">{formData.metaDescription.length}/160</p>
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium ${textSecondary} mb-2`}>
                                        Canonical URL (optional - for syndicated content)
                                    </label>
                                    <input
                                        type="text"
                                        name="canonicalUrl"
                                        value={formData.canonicalUrl}
                                        onChange={handleInputChange}
                                        placeholder="https://original-source.com/article"
                                        className="w-full bg-gray-800/50 border border-gray-700/50 rounded-md px-4 py-2 text-silver-light placeholder-gray-500 focus:outline-none focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            {/* Publishing Options */}
                            <div className="space-y-4 pt-4 border-t border-gray-800">
                                <h4 className={`text-sm font-medium ${textSecondary} uppercase tracking-wider`}>Publishing Options</h4>
                                
                                <div className="flex flex-wrap gap-6">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="isPublished"
                                            checked={formData.isPublished}
                                            onChange={handleInputChange}
                                            className="w-4 h-4 bg-gray-800 border-gray-600 rounded text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-900"
                                        />
                                        <span className={`ml-2 ${textSecondary}`}>Publish immediately</span>
                                    </label>
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="isFeatured"
                                            checked={formData.isFeatured}
                                            onChange={handleInputChange}
                                            className="w-4 h-4 bg-gray-800 border-gray-600 rounded text-yellow-600 focus:ring-yellow-500 focus:ring-offset-gray-900"
                                        />
                                        <span className={`ml-2 ${textSecondary}`}>Mark as featured</span>
                                    </label>
                                    <div className="flex items-center">
                                        <label className={`${textSecondary} mr-2`}>Display Order:</label>
                                        <input
                                            type="number"
                                            name="displayOrder"
                                            value={formData.displayOrder}
                                            onChange={handleInputChange}
                                            min="0"
                                            className="w-20 bg-gray-800/50 border border-gray-700/50 rounded-md px-3 py-1 text-silver-light focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                            </div>
                        </form>

                        {/* Modal Footer */}
                        <div className="flex justify-end gap-3 p-6 border-t border-gray-700">
                            <button
                                type="button"
                                onClick={() => { setShowEditor(false); resetForm(); }}
                                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                onClick={handleSubmit}
                                disabled={saving}
                                className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {saving ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Saving...
                                    </>
                                ) : (
                                    editingPost ? 'Update Post' : 'Create Post'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlogPage;
